import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { misc } from "app/themes/colors";

import GuideText from "./guide-text.component";
import ScribeGuideButton from "./scribe-guide-button.component";
import { SCRIBE_GUIDE_COPY as COPY } from "./scribe-guide.copy";

/**
 * What an empty Scribes grid says, in place of "No Medical Scribe records found":
 * - `never_recorded`: never recorded, and the inline guide is hidden or not offered (it is a
 *   trial's welcome, so a paid seat never gets it). The next step, plus a way into the guide.
 * - `filters`: has recorded, nothing matches. Names only the filters that exist.
 * - `patient`: the patient Scribes tab, which has its own Capture Visit button.
 */
export type ScribeGuideEmptyStateVariant =
  | "never_recorded"
  | "filters"
  | "patient";

interface ScribeGuideEmptyStateProps {
  variant: ScribeGuideEmptyStateVariant;
  /** `filters` only: whether the date filter is on screen (`AMBIENT_SCRIBE_LIST_FILTERS`). */
  hasDateFilter?: boolean;
  onOpenGuide?: () => void;
}

function texts(
  variant: ScribeGuideEmptyStateVariant,
  hasDateFilter: boolean
): { title: string; body: string } {
  switch (variant) {
    case "never_recorded":
      return { title: COPY.neverRecordedTitle, body: COPY.neverRecordedBody };
    case "patient":
      return { title: COPY.patientEmptyTitle, body: COPY.patientEmptyBody };
    case "filters":
      return {
        title: COPY.filterEmptyTitle,
        body: hasDateFilter
          ? COPY.filterEmptyBodyWithDates
          : COPY.filterEmptyBody
      };
  }
}

/**
 * The Scribes page's invitation to the tour, pitched at the reader: someone who has never recorded
 * hears what Scribe does for them, someone who has hears what else it can do. The patient tab keeps
 * a plain link: it has Capture Visit right above it, and its grid has less room.
 */
function inviteTexts(
  variant: ScribeGuideEmptyStateVariant
): { title: string; body: string } | null {
  switch (variant) {
    case "never_recorded":
      return { title: COPY.inviteNewTitle, body: COPY.inviteNewBody };
    case "filters":
      return { title: COPY.inviteMoreTitle, body: COPY.inviteMoreBody };
    case "patient":
      return null;
  }
}

export default function ScribeGuideEmptyState({
  variant,
  hasDateFilter = false,
  onOpenGuide
}: ScribeGuideEmptyStateProps) {
  const { title, body } = texts(variant, hasDateFilter);
  const invite = onOpenGuide ? inviteTexts(variant) : null;
  return (
    <Stack
      spacing={2.5}
      alignItems="center"
      justifyContent="center"
      // border-box: the grid's overlay is exactly the viewport's height, so padding added on top
      // of 100% overflows it and the grid grows both scrollbars.
      sx={{
        height: "100%",
        boxSizing: "border-box",
        px: 2.5,
        py: 3.5,
        textAlign: "center"
      }}
      data-testid="row-no-data-found"
    >
      <Stack spacing={1} alignItems="center">
        <Typography variant="body3" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ maxWidth: "46ch", lineHeight: 1.5 }}>
          <GuideText text={body} />
        </Typography>
        {onOpenGuide && !invite && (
          <Link
            component="button"
            type="button"
            variant="body2"
            fontWeight={700}
            onClick={onOpenGuide}
            sx={{ minHeight: 44 }}
          >
            {COPY.howScribeWorks}
          </Link>
        )}
      </Stack>
      {onOpenGuide && invite && (
        // Text and button side by side, so the invitation fits the grid's 280px minimum; on a
        // narrow screen the button wraps under the text.
        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
          gap={2}
          data-testid="scribe-guide-empty-invite"
          sx={{
            maxWidth: 640,
            boxSizing: "border-box",
            background: misc.lightLavender,
            borderLeft: `4px solid ${misc.suno}`,
            borderRadius: "8px",
            px: 2.5,
            py: 2,
            textAlign: "left"
          }}
        >
          <Box sx={{ flex: "1 1 260px" }}>
            <Typography variant="body2" component="p" fontWeight={700}>
              {invite.title}
            </Typography>
            <Typography variant="body2" component="p" sx={{ lineHeight: 1.5 }}>
              {invite.body}
            </Typography>
          </Box>
          <ScribeGuideButton onClick={onOpenGuide} />
        </Stack>
      )}
    </Stack>
  );
}
